import {
  useGetClicksReportQuery,
  useGetConversionReportQuery,
  useGetCostReportQuery,
  useGetOpportunityReportQuery,
  useGetRevenueReportQuery
} from 'app/apis/advanced-reports/advanced-report-query-api';
import { AdvancedReportsQueryCategories } from 'features/advanced-reports/constants/advanced-reports-query-categories';
import { SegmentTypes } from 'features/segment-controls/constants/segment-types';
import { v4 as guid } from 'uuid';
import { getCustomDataKey } from 'features/reports/helpers/report-type-helper';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { getSelectedAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import { AttributionModelTypes } from 'common/constants/attribution-model-types';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import {
  AdvancedReportMetricRow,
  GetAdvancedReportQuerySegment
} from 'features/advanced-reports/advanced-reports.types';
import {
  getSelectedAttributionCompareModel,
  selectIsAttributionComparisonActive
} from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import { toLocalIso } from 'common/utils/date-helpers';

interface ReportQueryResult {
  isFetching: boolean;
  isLoading: boolean;
  data: AdvancedReportMetricRow[];
}

export interface UseReportQueriesResult {
  main: {
    impressions: ReportQueryResult;
    clicks: ReportQueryResult;
    calls: ReportQueryResult;
    forms: ReportQueryResult;
    opps: ReportQueryResult;
    oppValues: ReportQueryResult;
    sales: ReportQueryResult;
    revenues: ReportQueryResult;
    cost: ReportQueryResult;
  };
  compare: {
    impressions: ReportQueryResult;
    clicks: ReportQueryResult;
    calls: ReportQueryResult;
    forms: ReportQueryResult;
    opps: ReportQueryResult;
    oppValues: ReportQueryResult;
    sales: ReportQueryResult;
    revenues: ReportQueryResult;
    cost: ReportQueryResult;
  };
}

export const useReportQueries = (
  reportType: string
): UseReportQueriesResult => {
  const dates = useSelector(getDateRangeAsObjects);
  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const isCompareSelected = useSelector(selectIsAttributionComparisonActive);

  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );

  const selectedAttributionModel = useSelector((state: RootState) => {
    const selectedAttributionModel = getSelectedAttributionModel(
      state,
      isPocModeActive
    );

    return selectedAttributionModel;
  });

  const { id: selectedAttributionCompareModel } = useSelector(
    (state: RootState) =>
      getSelectedAttributionCompareModel(state, isPocModeActive)
  );

  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const newGuid = useMemo(() => guid(), []);

  const getSegmentRequest = (
    reportType: string
  ): GetAdvancedReportQuerySegment[] => {
    return [
      {
        filters: [],
        order: 0,
        segmentCategory: getCustomDataKey(reportType),
        segmentId: newGuid,
        segmentType: SegmentTypes.CUSTOM_DATA_KEY
      }
    ];
  };

  const {
    data: impressionsCustomData,
    isLoading: isLoadingImpressions,
    isFetching: isFetchingImpressions
  } = useGetCostReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.IMPRESSIONS,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: webConversionsCustomData,
    isLoading: isLoadingWebConversions,
    isFetching: isFetchingWebConversions
  } = useGetConversionReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.CONVERSION_WEB,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: inboundCallConversionsCustomData,
    isLoading: isLoadingInboundCallConversions,
    isFetching: isFetchingInboundCallConversions
  } = useGetConversionReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: clicksCustomData,
    isLoading: isLoadingClicks,
    isFetching: isFetchingClicks
  } = useGetClicksReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.CLICKS,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: costCustomData,
    isLoading: isLoadingCost,
    isFetching: isFetchingCost
  } = useGetCostReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.AD_COST,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: revenueCustomData,
    isLoading: isLoadingRevenue,
    isFetching: isFetchingRevenue
  } = useGetRevenueReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.REVENUE,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: salesCustomData,
    isLoading: isLoadingSales,
    isFetching: isFetchingSales
  } = useGetRevenueReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.SALES,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: opportunityCustomData,
    isLoading: isLoadingOpportunities,
    isFetching: isFetchingOpportunities
  } = useGetOpportunityReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.OPPORTUNITY,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: opportunityValuesCustomData,
    isLoading: isLoadingOpportunityValues,
    isFetching: isFetchingOpportunityValues
  } = useGetOpportunityReportQuery({
    startDate: toLocalIso(dates.startDate),
    endDate: toLocalIso(dates.endDate),
    siteId,
    queryCategory: AdvancedReportsQueryCategories.OPPORTUNITY_VALUE,
    attributionModelName: selectedAttributionModel,
    currencyCode: selectedCurrencyCode,
    segmentRequests: getSegmentRequest(reportType)
  });

  const {
    data: impressionsCompareCustomData,
    isLoading: isLoadingCompareImpressions,
    isFetching: isFetchingCompareImpressions
  } = useGetCostReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.IMPRESSIONS,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: webConversionsCompareCustomData,
    isLoading: isLoadingCompareWebConversions,
    isFetching: isFetchingCompareWebConversions
  } = useGetConversionReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.CONVERSION_WEB,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: inboundCallConversionsCompareCustomData,
    isLoading: isLoadingCompareInboundCallConversions,
    isFetching: isFetchingCompareInboundCallConversions
  } = useGetConversionReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: clicksCompareCustomData,
    isLoading: isLoadingCompareClicks,
    isFetching: isFetchingCompareClicks
  } = useGetClicksReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.CLICKS,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: costCompareCustomData,
    isLoading: isLoadingCompareCost,
    isFetching: isFetchingCompareCost
  } = useGetCostReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.AD_COST,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: revenueCompareCustomData,
    isLoading: isLoadingCompareRevenue,
    isFetching: isFetchingCompareRevenue
  } = useGetRevenueReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.REVENUE,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: salesCompareCustomData,
    isLoading: isLoadingCompareSales,
    isFetching: isFetchingCompareSales
  } = useGetRevenueReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.SALES,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: opportunityCompareCustomData,
    isLoading: isLoadingCompareOpportunities,
    isFetching: isFetchingCompareOpportunities
  } = useGetOpportunityReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.OPPORTUNITY,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  const {
    data: opportunityValuesCompareCustomData,
    isLoading: isLoadingCompareOpportunityValues,
    isFetching: isFetchingCompareOpportunityValues
  } = useGetOpportunityReportQuery(
    {
      startDate: toLocalIso(dates.startDate),
      endDate: toLocalIso(dates.endDate),
      siteId,
      queryCategory: AdvancedReportsQueryCategories.OPPORTUNITY_VALUE,
      attributionModelName: selectedAttributionCompareModel,
      currencyCode: selectedCurrencyCode,
      segmentRequests: getSegmentRequest(reportType)
    },
    {
      skip: !isCompareSelected
    }
  );

  return {
    main: {
      impressions: {
        isFetching: isFetchingImpressions,
        isLoading: isLoadingImpressions,
        data: impressionsCustomData ?? []
      },
      clicks: {
        isFetching: isFetchingClicks,
        isLoading: isLoadingClicks,
        data: clicksCustomData ?? []
      },
      calls: {
        isFetching: isFetchingInboundCallConversions,
        isLoading: isLoadingInboundCallConversions,
        data: inboundCallConversionsCustomData ?? []
      },
      forms: {
        isFetching: isFetchingWebConversions,
        isLoading: isLoadingWebConversions,
        data: webConversionsCustomData ?? []
      },
      opps: {
        isFetching: isFetchingOpportunities,
        isLoading: isLoadingOpportunities,
        data: opportunityCustomData ?? []
      },
      oppValues: {
        isFetching: isFetchingOpportunityValues,
        isLoading: isLoadingOpportunityValues,
        data: opportunityValuesCustomData ?? []
      },
      sales: {
        isFetching: isFetchingSales,
        isLoading: isLoadingSales,
        data: salesCustomData ?? []
      },
      revenues: {
        isFetching: isFetchingRevenue,
        isLoading: isLoadingRevenue,
        data: revenueCustomData ?? []
      },
      cost: {
        isFetching: isFetchingCost,
        isLoading: isLoadingCost,
        data: costCustomData ?? []
      }
    },
    compare: {
      impressions: {
        isFetching: isFetchingCompareImpressions,
        isLoading: isLoadingCompareImpressions,
        data: impressionsCompareCustomData ?? []
      },
      clicks: {
        isFetching: isFetchingCompareClicks,
        isLoading: isLoadingCompareClicks,
        data: clicksCompareCustomData ?? []
      },
      calls: {
        isFetching: isFetchingCompareInboundCallConversions,
        isLoading: isLoadingCompareInboundCallConversions,
        data: inboundCallConversionsCompareCustomData ?? []
      },
      forms: {
        isFetching: isFetchingCompareWebConversions,
        isLoading: isLoadingCompareWebConversions,
        data: webConversionsCompareCustomData ?? []
      },
      opps: {
        isFetching: isFetchingCompareOpportunities,
        isLoading: isLoadingCompareOpportunities,
        data: opportunityCompareCustomData ?? []
      },
      oppValues: {
        isFetching: isFetchingCompareOpportunityValues,
        isLoading: isLoadingCompareOpportunityValues,
        data: opportunityValuesCompareCustomData ?? []
      },
      sales: {
        isFetching: isFetchingCompareSales,
        isLoading: isLoadingCompareSales,
        data: salesCompareCustomData ?? []
      },
      revenues: {
        isFetching: isFetchingCompareRevenue,
        isLoading: isLoadingCompareRevenue,
        data: revenueCompareCustomData ?? []
      },
      cost: {
        isFetching: isFetchingCompareCost,
        isLoading: isLoadingCompareCost,
        data: costCompareCustomData ?? []
      }
    }
  };
};
